import type { PrismicImage } from '@virginexperiencedays/cms';
import { cn } from '@virginexperiencedays/tailwind';

import { Image } from '../../../layout/Image';
import { VisualHeading } from '../../../typography/VisualHeading';

type Author = {
  name: string;
  bio: string;
  avatar?: PrismicImage;
};

export type AuthorBioModuleProps = {
  /**
   * @param {PrismicImage} avatar - Author's avatar url
   * @param {string} name - Author's name
   * @param {string} bio - Author's bio
   */
  author: Author;
  className?: string;
  imageClassName?: string;
};

export const AuthorBioModule = ({ author, className, imageClassName }: AuthorBioModuleProps) => {
  const { name, avatar, bio } = author;
  return (
    // Nonsense to use grid as we would have to overwrite a lot of options anyway
    <div
      className={cn(
        'bg-background-primary-faded mb-10 mt-0 grid grid-cols-[minmax(0,48px)_1fr] rounded-sm p-6 md:grid-cols-[minmax(0,80px)_1fr]',
        className
      )}
    >
      {avatar?.url && (
        <Image
          className={cn(
            'col-start-1 col-end-2 row-start-1 row-end-2 self-start rounded-full md:row-end-3 md:max-h-[80px] md:max-w-[80px]',
            imageClassName
          )}
          src={avatar.url}
          alt={name}
          imageObject={avatar}
          width={avatar?.dimensions?.width ?? 80}
          height={avatar?.dimensions?.height ?? 80}
          sizes="5vw"
          dataTestId="abm-author-image"
        />
      )}
      <VisualHeading
        className="text-neutral col-start-2 col-end-3 row-start-1 row-end-2 mx-4 my-2 self-center text-xl font-normal leading-tight md:my-0 md:self-end"
        size="6"
        as="h2"
        data-testid="abm-author-name"
      >
        {name}
      </VisualHeading>
      <p
        className="text-neutral col-start-1 col-end-3 row-start-2 row-end-3 mt-4 text-base leading-normal md:col-start-2 md:mt-2 md:self-start md:px-4 md:text-sm"
        data-testid="abm-author-bio"
      >
        {bio}
      </p>
    </div>
  );
};
